/**
 * AI Service
 * 
 * Handles API calls for AI-powered competitive analysis features.
 * Admin-only service for testing AI analysis before client rollout.
 */

import { auth } from '../firebase';
import config from '../config';

/**
 * Generate AI competitive analysis for two schools
 * @param {string} selectedSchoolUuid - Primary school UUID
 * @param {string} comparisonSchoolUuid - Competitor school UUID
 * @param {boolean} forceRefresh - Skip cache and generate fresh analysis
 * @param {string} verbosity - Output detail level: 'low', 'medium', or 'high'
 * @param {string} reasoningEffort - Thinking depth: 'minimal', 'medium', or 'high'
 * @returns {Promise<Object>} AI analysis with text, sources, and metadata
 */
export const getCompetitiveAnalysis = async (
  selectedSchoolUuid, 
  comparisonSchoolUuid, 
  forceRefresh = false,
  verbosity = 'medium',
  reasoningEffort = 'medium'
) => {
  try {
    // Get current user token
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const params = new URLSearchParams({
      selected_uuid: selectedSchoolUuid,
      comparison_uuid: comparisonSchoolUuid,
      force_refresh: forceRefresh.toString(),
      verbosity: verbosity,
      reasoning_effort: reasoningEffort
    });
    
    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/competitive-analysis?${params}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to generate AI analysis');
    }
    
    return await response.json();
  } catch (error) {
    console.error('AI Service Error:', error);
    throw error;
  }
};

/**
 * Get AI usage statistics (admin only)
 * @returns {Promise<Object>} Usage stats including costs and request counts
 */
export const getAIUsageStats = async () => {
  try {
    // Get current user token
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/usage-stats`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch usage stats');
    }
    
    return await response.json();
  } catch (error) {
    console.error('AI Stats Error:', error);
    throw error;
  }
};

/**
 * Generate AI geographic summary for a school
 * @param {string} uuid - School UUID
 * @param {number} driveTime - Drive time in minutes (5, 10, or 15)
 * @param {Array<string>} grades - Selected grades
 * @param {Array<string>} governance - Governance filters (e.g., ['Charter', 'District', 'Private'])
 * @param {Array<string>} schoolType - School type filters
 * @param {boolean} forceRefresh - Skip cache and generate fresh analysis
 * @returns {Promise<Object>} Geographic summary with data, narrative, and visualizations
 */
export const getGeographicSummary = async (
  uuid,
  driveTime = 10,
  grades = [],
  governance = ['Charter', 'District', 'Private'],
  schoolType = [],
  forceRefresh = false,
  verbosity = 'medium',
  reasoningEffort = 'medium'
) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const params = new URLSearchParams({
      uuid,
      drive_time: driveTime.toString(),
      force_refresh: forceRefresh.toString(),
      verbosity,
      reasoning_effort: reasoningEffort
    });
    
    // Add array parameters
    grades.forEach(grade => params.append('grades', grade));
    governance.forEach(gov => params.append('governance', gov));
    schoolType.forEach(type => params.append('school_type', type));
    
    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/geographic-summary?${params}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to generate geographic summary');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Geographic Summary Error:', error);
    throw error;
  }
};

/**
 * Generate AI competitive summary for a school
 * @param {string} uuid - School UUID
 * @param {number} driveTime - Drive time in minutes (5, 10, or 15)
 * @param {Array<string>} grades - Selected grades
 * @param {Array<string>} governance - Governance filters
 * @param {Array<string>} schoolType - School type filters
 * @param {boolean} forceRefresh - Skip cache and generate fresh analysis
 * @returns {Promise<Object>} Competitive summary with data, narrative, and visualizations
 */
export const getCompetitiveSummary = async (
  uuid,
  driveTime = 10,
  grades = [],
  governance = ['Charter', 'District', 'Private'],
  schoolType = [],
  forceRefresh = false,
  verbosity = 'medium',
  reasoningEffort = 'medium'
) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const params = new URLSearchParams({
      uuid,
      drive_time: driveTime.toString(),
      force_refresh: forceRefresh.toString(),
      verbosity,
      reasoning_effort: reasoningEffort
    });
    
    // Add array parameters
    grades.forEach(grade => params.append('grades', grade));
    governance.forEach(gov => params.append('governance', gov));
    schoolType.forEach(type => params.append('school_type', type));
    
    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/competitive-summary?${params}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to generate competitive summary');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Competitive Summary Error:', error);
    throw error;
  }
};

/**
 * Generate AI projections summary for a school
 * @param {string} uuid - School UUID
 * @param {Array<string>} grades - Selected grades
 * @param {boolean} forceRefresh - Skip cache and generate fresh analysis
 * @returns {Promise<Object>} Projections summary with data, narrative, and visualizations
 */
export const getProjectionsSummary = async (
  uuid,
  grades = [],
  forceRefresh = false,
  verbosity = 'medium',
  reasoningEffort = 'medium'
) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const params = new URLSearchParams({
      uuid,
      force_refresh: forceRefresh.toString(),
      verbosity,
      reasoning_effort: reasoningEffort
    });
    
    // Add array parameters
    grades.forEach(grade => params.append('grades', grade));
    
    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/projections-summary?${params}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to generate projections summary');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Projections Summary Error:', error);
    throw error;
  }
};

// =============================================================================
// USER-FACING: Check AI Summary Availability
// =============================================================================

/**
 * Check if the current user has access to any AI summaries across their team's schools.
 * This is used for conditional UI rendering (sidebar icon, route access).
 * @returns {Promise<Object>} { has_summaries: boolean, school_count: number, is_admin: boolean }
 */
export const checkUserHasAISummaries = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const response = await fetch(
      `${config.apiUrl}/v2/user/has-ai-summaries`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to check AI summaries availability');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Check User Has AI Summaries Error:', error);
    // Return false by default on error
    return { has_summaries: false, school_count: 0, is_admin: false };
  }
};

/**
 * Get list of school UUIDs that have AI summaries for the current user.
 * This is used to filter the school list in the AI Summary tool.
 * @returns {Promise<Object>} { school_uuids: Array<string>, is_admin: boolean }
 */
export const getSchoolsWithAISummaries = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const response = await fetch(
      `${config.apiUrl}/v2/user/schools-with-ai-summaries`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get schools with AI summaries');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Get Schools With AI Summaries Error:', error);
    // Return empty list by default on error
    return { schools: [], is_admin: false, portfolios: [] };
  }
};

/**
 * List published multi-school portfolios visible to the current user.
 * @returns {Promise<Object>} { success, portfolios: [...] }
 */
export const getUserMultiSchoolPortfolios = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();

    const response = await fetch(
      `${config.apiUrl}/v2/user/ai/multi-school`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get multi-school portfolios');
    }

    return await response.json();
  } catch (error) {
    console.error('Get User Multi-School Portfolios Error:', error);
    throw error;
  }
};

/**
 * Fetch stored multi-school summaries for a published portfolio.
 * @param {string} schoolSetHash
 * @param {number} driveTime
 * @param {string} travelMode
 * @returns {Promise<Object>} { success, summaries, school_uuids, ... }
 */
export const getUserStoredMultiSchoolSummaries = async (
  schoolSetHash,
  driveTime = 10,
  travelMode = 'drive'
) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();

    const params = new URLSearchParams({
      school_set_hash: schoolSetHash,
      drive_time: driveTime.toString(),
      travel_mode: travelMode
    });

    const response = await fetch(
      `${config.apiUrl}/v2/user/ai/multi-school/stored?${params}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get stored multi-school summaries');
    }

    return await response.json();
  } catch (error) {
    console.error('Get User Stored Multi-School Summaries Error:', error);
    throw error;
  }
};

// =============================================================================
// USER-FACING: Fetch Pre-generated Summaries
// =============================================================================

/**
 * Get ALL pre-generated AI summaries for a school in ONE call (faster than individual calls)
 * @param {string} uuid - School UUID
 * @param {number} driveTime - Drive time in minutes (5, 10, 15, 20, 25, or 30)
 * @param {string} travelMode - Travel mode: 'drive' or 'walk'
 * @returns {Promise<Object>} All stored summaries for this school/drive time/travel mode
 */
export const getAllStoredSummaries = async (uuid, driveTime = 10, travelMode = 'drive') => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const params = new URLSearchParams({
      drive_time: driveTime.toString(),
      travel_mode: travelMode
    });
    
    const response = await fetch(
      `${config.apiUrl}/v2/schools/${uuid}/ai-summaries?${params}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch summaries');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Get All Stored Summaries Error:', error);
    throw error;
  }
};

/**
 * Get a pre-generated AI summary for a school (single type)
 * @param {string} uuid - School UUID
 * @param {number} driveTime - Drive time in minutes (5, 10, or 15)
 * @param {string} summaryType - Summary type: 'geographic', 'competitive', 'projections', or 'combined'
 * @returns {Promise<Object>} Stored summary or not found response
 */
export const getStoredSummary = async (uuid, driveTime = 10, summaryType = 'geographic', travelMode = 'drive') => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const params = new URLSearchParams({
      drive_time: driveTime.toString(),
      type: summaryType,
      travel_mode: travelMode
    });
    
    const response = await fetch(
      `${config.apiUrl}/v2/schools/${uuid}/ai-summary?${params}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    // Handle 404 gracefully - summary not found
    if (response.status === 404) {
      const data = await response.json();
      return { found: false, ...data };
    }
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch summary');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Get Stored Summary Error:', error);
    throw error;
  }
};

/**
 * Get raw data for user-facing visualizations alongside stored summaries
 * @param {string} uuid - School UUID
 * @param {number} driveTime - Drive time in minutes (5, 10, 15, 20, 25, or 30)
 * @param {Array<string>} summaryTypes - Which data to fetch: 'geographic', 'competitive', 'projections'
 * @param {string} travelMode - Travel mode: 'drive' or 'walk'
 * @returns {Promise<Object>} Raw data for visualizations
 */
export const getUserSummaryData = async (uuid, driveTime = 10, summaryTypes = ['geographic', 'competitive', 'projections'], travelMode = 'drive') => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const params = new URLSearchParams({
      drive_time: driveTime.toString(),
      travel_mode: travelMode
    });
    summaryTypes.forEach(type => params.append('summary_types', type));
    
    const response = await fetch(
      `${config.apiUrl}/v2/schools/${uuid}/summary-data?${params}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch summary data');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Get User Summary Data Error:', error);
    throw error;
  }
};

/**
 * Get all pre-generated AI summaries for a school
 * @param {string} uuid - School UUID
 * @param {number} driveTime - Optional: filter by drive time
 * @returns {Promise<Object>} All available summaries for the school
 */
export const getStoredSummaries = async (uuid, driveTime = null) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const params = new URLSearchParams();
    if (driveTime) {
      params.set('drive_time', driveTime.toString());
    }
    
    const response = await fetch(
      `${config.apiUrl}/v2/schools/${uuid}/ai-summaries?${params}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch summaries');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Get Stored Summaries Error:', error);
    throw error;
  }
};

/**
 * Get summary status for a school (what's available)
 * @param {string} uuid - School UUID
 * @returns {Promise<Object>} Status of available summaries
 */
export const getSummaryStatus = async (uuid) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const response = await fetch(
      `${config.apiUrl}/v2/schools/${uuid}/ai-summary/status`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch summary status');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Get Summary Status Error:', error);
    throw error;
  }
};

// =============================================================================
// ADMIN-ONLY: Generate and Store Summaries
// =============================================================================

/**
 * Generate and store a single AI summary type for a school (admin only)
 * This is memory-efficient as it only processes one summary at a time.
 * @param {string} uuid - School UUID
 * @param {number} driveTime - Drive time in minutes (5, 10, or 15)
 * @param {string} summaryType - One of 'geographic', 'competitive', 'projections', 'combined'
 * @returns {Promise<Object>} Status of generated summary
 */
export const generateSingleSummary = async (uuid, driveTime = 10, summaryType) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/generate-single`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          uuid,
          drive_time: driveTime,
          summary_type: summaryType
        })
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `Failed to generate ${summaryType} summary`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Generate Single Summary Error (${summaryType}):`, error);
    throw error;
  }
};

/**
 * Generate all 4 summaries sequentially (memory-efficient for App Engine)
 * Calls generate-single 4 times: geographic, competitive, projections, then combined.
 * @param {string} uuid - School UUID
 * @param {number} driveTime - Drive time in minutes (5, 10, or 15)
 * @param {function} onProgress - Optional callback for progress updates: (step, total, type, result) => void
 * @returns {Promise<Object>} Status of all generated summaries
 */
export const generateAllSummariesSequentially = async (uuid, driveTime = 10, onProgress = null) => {
  const summaryTypes = ['geographic', 'competitive', 'projections', 'combined'];
  const results = {
    success: true,
    school_uuid: uuid,
    drive_time: driveTime,
    summaries: {}
  };
  
  for (let i = 0; i < summaryTypes.length; i++) {
    const summaryType = summaryTypes[i];
    
    try {
      // Call progress callback before starting
      if (onProgress) {
        onProgress(i + 1, summaryTypes.length, summaryType, null);
      }
      
      const result = await generateSingleSummary(uuid, driveTime, summaryType);
      results.summaries[summaryType] = {
        success: true,
        tokens_used: result.tokens_used || 0
      };
      
      // Call progress callback after completion
      if (onProgress) {
        onProgress(i + 1, summaryTypes.length, summaryType, result);
      }
      
    } catch (error) {
      console.error(`Failed to generate ${summaryType} summary:`, error);
      results.summaries[summaryType] = {
        success: false,
        error: error.message
      };
      // Don't set overall success to false for individual failures
      // Continue generating remaining summaries
    }
  }
  
  // Check if any summaries failed
  const failedCount = Object.values(results.summaries).filter(s => !s.success).length;
  if (failedCount === summaryTypes.length) {
    results.success = false;
  }
  
  return results;
};

/**
 * Generate all summaries in parallel for multiple drive times (much faster)
 * Phase 1: Generates geographic, competitive, projections for all drive times (9 calls in parallel)
 * Phase 2: Generates combined summaries for all drive times (3 calls in parallel)
 * 
 * @param {string} uuid - School UUID
 * @param {Array<number>} driveTimes - Array of drive times in minutes (e.g., [5, 10, 15])
 * @param {function} onProgress - Optional callback: (phase, completedCount, totalCount, results) => void
 * @returns {Promise<Object>} Status of all generated summaries
 */
export const generateAllSummariesParallel = async (uuid, driveTimes = [5, 10, 15], onProgress = null, isDropAPin = false) => {
  const startTime = Date.now();
  const allResults = {
    success: true,
    school_uuid: uuid,
    drive_times: driveTimes,
    summaries: {},
    phases: {},
    is_drop_a_pin: isDropAPin
  };

  try {
    // Phase 1: For drop-a-pins, only generate geographic summaries
    // For regular schools, generate geographic, competitive, projections for ALL drive times
    const phase1Types = isDropAPin ? ['geographic'] : ['geographic', 'competitive', 'projections'];
    const phase1Promises = [];
    const phase1Metadata = []; // Track which call is which
    
    for (const driveTime of driveTimes) {
      for (const summaryType of phase1Types) {
        phase1Metadata.push({ driveTime, summaryType });
        phase1Promises.push(
          generateSingleSummary(uuid, driveTime, summaryType)
            .then(result => ({ success: true, driveTime, summaryType, result }))
            .catch(error => ({ success: false, driveTime, summaryType, error: error.message }))
        );
      }
    }

    if (onProgress) {
      onProgress('phase1', 0, phase1Promises.length, {});
    }

    // Execute Phase 1 in parallel
    const phase1Results = await Promise.allSettled(phase1Promises);
    
    // Process Phase 1 results
    const phase1Completed = [];
    const phase1Failed = [];
    
    phase1Results.forEach((result, index) => {
      const metadata = phase1Metadata[index];
      const key = `${metadata.summaryType}_${metadata.driveTime}`;
      
      if (result.status === 'fulfilled' && result.value.success) {
        allResults.summaries[key] = {
          success: true,
          tokens_used: result.value.result.tokens_used || 0,
          drive_time: metadata.driveTime,
          summary_type: metadata.summaryType
        };
        phase1Completed.push(key);
      } else {
        const errorMsg = result.status === 'rejected' 
          ? result.reason?.message || 'Unknown error'
          : result.value?.error || 'Unknown error';
        allResults.summaries[key] = {
          success: false,
          error: errorMsg,
          drive_time: metadata.driveTime,
          summary_type: metadata.summaryType
        };
        phase1Failed.push(key);
      }
    });

    allResults.phases.phase1 = {
      completed: phase1Completed.length,
      failed: phase1Failed.length,
      total: phase1Promises.length
    };

    if (onProgress) {
      onProgress('phase1', phase1Completed.length, phase1Promises.length, allResults.summaries);
    }

    // Skip Phase 2 (combined summaries) for drop-a-pin locations
    if (isDropAPin) {
      console.log('[generateAllSummariesParallel] Skipping Phase 2 for drop-a-pin location');
      allResults.phases.phase2 = {
        completed: 0,
        failed: 0,
        total: 0,
        skipped: true
      };
      
      // Calculate summary count
      const successful = Object.values(allResults.summaries).filter(s => s.success).length;
      const failed = Object.values(allResults.summaries).filter(s => !s.success).length;
      
      allResults.summary_count = {
        successful,
        failed,
        total: successful + failed
      };
      
      allResults.elapsed_seconds = ((Date.now() - startTime) / 1000).toFixed(1);
      
      return allResults;
    }

    // Wait for database commits to complete before starting Phase 2
    console.log('[generateAllSummariesParallel] Phase 1 complete. Waiting 3 seconds for database commits...');
    await new Promise(resolve => setTimeout(resolve, 3000));

    // Phase 2: Generate combined summaries SEQUENTIALLY (not in parallel)
    // This ensures each combined summary is generated only after verifying base summaries exist
    const phase2DriveTimesToProcess = [];
    
    for (const driveTime of driveTimes) {
      // Check if at least one base summary succeeded for this drive time
      const hasBaseSummaries = ['geographic', 'competitive', 'projections'].some(type => 
        allResults.summaries[`${type}_${driveTime}`]?.success
      );
      
      if (hasBaseSummaries) {
        phase2DriveTimesToProcess.push(driveTime);
      }
    }

    const phase2Completed = [];
    const phase2Failed = [];
    const phase2Total = phase2DriveTimesToProcess.length;

    if (phase2Total > 0) {
      if (onProgress) {
        onProgress('phase2', 0, phase2Total, allResults.summaries);
      }

      // Process combined summaries sequentially with verification and retry
      for (let i = 0; i < phase2DriveTimesToProcess.length; i++) {
        const driveTime = phase2DriveTimesToProcess[i];
        const key = `combined_${driveTime}`;
        
        console.log(`[generateAllSummariesParallel] Phase 2: Processing combined summary ${i + 1}/${phase2Total} for drive time ${driveTime}min`);
        
        // Verify base summaries exist in database before generating combined
        let verified = false;
        let retryCount = 0;
        const maxRetries = 3;
        const retryDelays = [2000, 4000, 8000]; // Exponential backoff
        
        while (!verified && retryCount <= maxRetries) {
          if (retryCount > 0) {
            const delay = retryDelays[retryCount - 1];
            console.log(`[generateAllSummariesParallel] Retry ${retryCount}/${maxRetries}: Waiting ${delay}ms before verifying summaries...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
          
          // Check if all base summaries exist in database
          const baseSummaryTypes = ['geographic', 'competitive', 'projections'];
          const verificationResults = await Promise.all(
            baseSummaryTypes.map(async (type) => {
              try {
                const result = await getStoredSummary(uuid, driveTime, type);
                return result.found !== false; // Summary exists if found is not false
              } catch (error) {
                console.error(`[generateAllSummariesParallel] Error checking ${type} summary:`, error);
                return false;
              }
            })
          );
          
          const allBaseSummariesExist = verificationResults.every(exists => exists);
          
          if (allBaseSummariesExist) {
            verified = true;
            console.log(`[generateAllSummariesParallel] Verified: All base summaries exist for drive time ${driveTime}min`);
          } else {
            retryCount++;
            const missing = baseSummaryTypes.filter((_, idx) => !verificationResults[idx]);
            console.warn(`[generateAllSummariesParallel] Missing base summaries for drive time ${driveTime}min: ${missing.join(', ')}`);
          }
        }
        
        // Generate combined summary if verified, or record failure
        if (verified) {
          try {
            const result = await generateSingleSummary(uuid, driveTime, 'combined');
            allResults.summaries[key] = {
              success: true,
              tokens_used: result.tokens_used || 0,
              drive_time: driveTime,
              summary_type: 'combined'
            };
            phase2Completed.push(key);
            console.log(`[generateAllSummariesParallel] Successfully generated combined summary for drive time ${driveTime}min`);
          } catch (error) {
            const errorMsg = error.message || 'Unknown error';
            allResults.summaries[key] = {
              success: false,
              error: errorMsg,
              drive_time: driveTime,
              summary_type: 'combined'
            };
            phase2Failed.push(key);
            console.error(`[generateAllSummariesParallel] Failed to generate combined summary for drive time ${driveTime}min:`, error);
          }
        } else {
          // Failed to verify after all retries
          allResults.summaries[key] = {
            success: false,
            error: `Base summaries not available in database after ${maxRetries} retries. This may indicate a database commit delay or failure.`,
            drive_time: driveTime,
            summary_type: 'combined'
          };
          phase2Failed.push(key);
          console.error(`[generateAllSummariesParallel] Could not verify base summaries for drive time ${driveTime}min after ${maxRetries} retries`);
        }
        
        // Update progress after each combined summary
        if (onProgress) {
          onProgress('phase2', i + 1, phase2Total, allResults.summaries);
        }
      }

      allResults.phases.phase2 = {
        completed: phase2Completed.length,
        failed: phase2Failed.length,
        total: phase2Total
      };

      console.log(`[generateAllSummariesParallel] Phase 2 complete: ${phase2Completed.length} succeeded, ${phase2Failed.length} failed`);
    } else {
      allResults.phases.phase2 = {
        completed: 0,
        failed: 0,
        total: 0,
        skipped: true,
        reason: 'No base summaries available'
      };
      console.log('[generateAllSummariesParallel] Phase 2 skipped: No base summaries available');
    }

    // Calculate overall success
    const totalSummaries = Object.keys(allResults.summaries).length;
    const successfulSummaries = Object.values(allResults.summaries).filter(s => s.success).length;
    const failedSummaries = totalSummaries - successfulSummaries;
    
    allResults.success = successfulSummaries > 0;
    allResults.summary_count = {
      total: totalSummaries,
      successful: successfulSummaries,
      failed: failedSummaries
    };
    allResults.total_time_seconds = ((Date.now() - startTime) / 1000).toFixed(1);

    return allResults;
    
  } catch (error) {
    console.error('Error in parallel summary generation:', error);
    allResults.success = false;
    allResults.error = error.message;
    return allResults;
  }
};

/**
 * Generate all summaries for a school and store in database (admin only)
 * NOTE: This calls all 4 in a single request which may cause OOM on App Engine.
 * Use generateAllSummariesSequentially for production use.
 * @param {string} uuid - School UUID
 * @param {number} driveTime - Drive time in minutes (5, 10, or 15)
 * @param {boolean} allDriveTimes - Generate for all three drive times
 * @returns {Promise<Object>} Status of generated summaries
 * @deprecated Use generateAllSummariesSequentially instead for App Engine compatibility
 */
export const generateAndStoreSummary = async (uuid, driveTime = 10, allDriveTimes = false) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/generate-and-store`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          uuid,
          drive_time: driveTime,
          all_drive_times: allDriveTimes
        })
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to generate and store summaries');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Generate and Store Error:', error);
    throw error;
  }
};

/**
 * Regenerate projections and overview AI summaries for a school.
 * Used when enrollment data or grade capacities are updated by users.
 * Unlike generateAndStoreSummary, this does NOT require admin role.
 * 
 * @param {string} uuid - School UUID
 * @param {number} driveTime - Drive time in minutes (5, 10, or 15)
 * @returns {Promise<Object>} Status of regenerated summaries
 */
export const regenerateProjectionsSummary = async (uuid, driveTime = 10) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/regenerate-projections-summary`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          uuid,
          drive_time: driveTime
        })
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to regenerate projections summary');
    }
    
    const result = await response.json();
    
    // Check if regeneration was skipped due to incomplete summary set
    if (result.skipped) {
      throw new Error('School does not have a complete set of AI summaries');
    }
    
    return result;
  } catch (error) {
    console.error('Regenerate Projections Summary Error:', error);
    throw error;
  }
};

/**
 * Save already-generated summaries to the database (admin only)
 * This saves the current preview without regenerating
 * @param {string} uuid - School UUID
 * @param {number} driveTime - Drive time in minutes (5, 10, or 15)
 * @param {Object} summaries - Object containing geographic, competitive, projections summaries
 * @returns {Promise<Object>} Status of saved summaries
 */
export const saveCurrentSummaries = async (uuid, driveTime, summaries) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/save-current`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          uuid,
          drive_time: driveTime,
          summaries
        })
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to save current summaries');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Save Current Summaries Error:', error);
    throw error;
  }
};

/**
 * Batch generate summaries for multiple schools (admin only)
 * @param {Array<string>} schoolUuids - List of school UUIDs
 * @param {Array<number>} driveTimes - List of drive times to generate
 * @returns {Promise<Object>} Status of batch generation
 */
export const batchGenerateSummaries = async (schoolUuids, driveTimes = [10]) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/batch-generate`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          school_uuids: schoolUuids,
          drive_times: driveTimes
        })
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to batch generate summaries');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Batch Generate Error:', error);
    throw error;
  }
};

/**
 * Get summary status for multiple schools (admin only)
 * @param {Array<string>} schoolUuids - List of school UUIDs
 * @returns {Promise<Object>} Status of summaries for each school
 */
export const getSchoolsSummaryStatus = async (schoolUuids) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/summary-status?school_uuids=${schoolUuids.join(',')}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get summary status');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Get Schools Summary Status Error:', error);
    throw error;
  }
};

/**
 * Get stored summaries for a school from database (admin only)
 * @param {string} uuid - School UUID
 * @param {number} driveTime - Optional drive time filter
 * @returns {Promise<Object>} Stored summaries with generation dates
 */
export const getStoredSummariesAdmin = async (uuid, driveTime = null) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    let url = `${config.apiUrl}/v2/admin/ai/stored-summaries/${uuid}`;
    if (driveTime) {
      url += `?drive_time=${driveTime}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      if (response.status === 404) {
        return { found: false };
      }
      const error = await response.json();
      throw new Error(error.error || 'Failed to get stored summaries');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Get Stored Summaries Admin Error:', error);
    throw error;
  }
};

/**
 * Get summary data only (without AI narratives) for visualizations
 * This allows loading stored narratives while having fresh data for charts/tables.
 * @param {string} uuid - School UUID
 * @param {number} driveTime - Drive time in minutes
 * @param {Array<string>} summaryTypes - Which data to fetch: 'geographic', 'competitive', 'projections'
 * @param {Array<string>} grades - Selected grades
 * @param {Array<string>} governance - Governance filters
 * @returns {Promise<Object>} Data for visualizations
 */
export const getSummaryDataOnly = async (uuid, driveTime = 10, summaryTypes = ['geographic', 'competitive', 'projections'], grades = [], governance = []) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const params = new URLSearchParams({
      uuid,
      drive_time: driveTime.toString()
    });
    
    // Add array parameters
    summaryTypes.forEach(type => params.append('summary_types', type));
    grades.forEach(grade => params.append('grades', grade));
    governance.forEach(gov => params.append('governance', gov));
    
    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/summary-data?${params}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get summary data');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Get Summary Data Error:', error);
    throw error;
  }
};

/**
 * Generate summaries for all schools in a team (admin only)
 * @param {string} teamId - Team ID
 * @param {boolean} allDriveTimes - Generate for all drive times
 * @param {Object} options - Optional: { stop_on_verification_failure }
 * @returns {Promise<Object>} Generation status
 */
export const generateForTeam = async (teamId, allDriveTimes = true, options = {}) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const body = {
      team_id: teamId,
      all_drive_times: allDriveTimes
    };
    if (options.stop_on_verification_failure !== undefined) {
      body.stop_on_verification_failure = options.stop_on_verification_failure;
    }
    
    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/generate-for-team`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to generate for team');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Generate For Team Error:', error);
    throw error;
  }
};

/**
 * Generate summaries for team with streaming progress via Server-Sent Events.
 * Calls onSchoolComplete for each school as it finishes, then resolves with final payload.
 * @param {string} teamId - Team ID
 * @param {boolean} allDriveTimes - Generate for all drive times
 * @param {Object} options - Optional: { stop_on_verification_failure }
 * @param {function} onSchoolComplete - (result) => void, called for each completed school
 * @returns {Promise<Object>} Final generation status (same shape as generateForTeam)
 */
export const generateForTeamStream = async (teamId, allDriveTimes = true, options = {}, onSchoolComplete) => {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  const token = await user.getIdToken();
  const body = {
    team_id: teamId,
    all_drive_times: allDriveTimes
  };
  if (options.stop_on_verification_failure !== undefined) {
    body.stop_on_verification_failure = options.stop_on_verification_failure;
  }

  const response = await fetch(
    `${config.apiUrl}/v2/admin/ai/generate-for-team-stream`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to generate for team');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalPayload = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const line = part.split('\n').find(l => l.startsWith('data: '));
      if (!line) continue;
      try {
        const data = JSON.parse(line.slice(6));
        if (data.type === 'school_complete' && data.result && onSchoolComplete) {
          onSchoolComplete(data.result);
        } else if (data.type === 'done' && data.payload) {
          finalPayload = data.payload;
        }
      } catch (e) {
        // skip malformed
      }
    }
  }
  if (buffer.trim()) {
    const line = buffer.split('\n').find(l => l.startsWith('data: '));
    if (line) {
      try {
        const data = JSON.parse(line.slice(6));
        if (data.type === 'done' && data.payload) finalPayload = data.payload;
      } catch (e) {}
    }
  }
  return finalPayload;
};

/**
 * Generate summaries for a batch of EDC schools with streaming progress.
 * @param {boolean} allDriveTimes - Generate for all drive times
 * @param {number} limit - Max schools per batch
 * @param {number} offset - Pagination offset
 * @param {Object} options - Optional batch filter flags
 * @param {function} onBatchStart - (payload) => void, called with { total_count, batch_number, total_batches }
 * @param {function} onSchoolComplete - (result) => void, called for each completed school
 * @returns {Promise<Object>} Batch result (same shape as generateForAll)
 */
export const generateForAllStream = async (
  allDriveTimes = true,
  limit = 250,
  offset = 0,
  options = {},
  onBatchStart,
  onSchoolComplete
) => {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  const token = await user.getIdToken();
  const body = {
    all_drive_times: allDriveTimes,
    limit,
    offset,
    ...options
  };

  const response = await fetch(
    `${config.apiUrl}/v2/admin/ai/generate-for-all-stream`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to generate for all schools');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalPayload = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const line = part.split('\n').find(l => l.startsWith('data: '));
      if (!line) continue;
      try {
        const data = JSON.parse(line.slice(6));
        if (data.type === 'batch_start' && data.payload && onBatchStart) {
          onBatchStart(data.payload);
        } else if (data.type === 'school_complete' && data.result && onSchoolComplete) {
          onSchoolComplete(data.result);
        } else if (data.type === 'done' && data.payload) {
          finalPayload = data.payload;
        }
      } catch (e) {}
    }
  }
  if (buffer.trim()) {
    const line = buffer.split('\n').find(l => l.startsWith('data: '));
    if (line) {
      try {
        const data = JSON.parse(line.slice(6));
        if (data.type === 'done' && data.payload) finalPayload = data.payload;
      } catch (e) {}
    }
  }
  return finalPayload;
};

/**
 * Generate summaries for all schools in EDC (admin only)
 * @param {boolean} allDriveTimes - Generate for all drive times
 * @param {number} limit - Max schools per batch
 * @param {number} offset - Pagination offset
 * @param {Object} options - Optional batch filter flags
 * @returns {Promise<Object>} Generation status with pagination info
 */
export const generateForAll = async (
  allDriveTimes = true,
  limit = 250,
  offset = 0,
  options = {}
) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/generate-for-all`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          all_drive_times: allDriveTimes,
          limit,
          offset,
          ...options
        })
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to generate for all schools');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Generate For All Error:', error);
    throw error;
  }
};

/**
 * Get summary coverage overview across all schools
 * Admin-only endpoint to see which schools have summaries
 * 
 * @returns {Promise<Object>} Coverage statistics and list of schools with their summary status
 */
export const getSummaryCoverage = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/summary-coverage`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch summary coverage');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Get Summary Coverage Error:', error);
    throw error;
  }
};

/**
 * Get quality report for all AI summaries
 * @param {string} severity - Filter by severity: 'all', 'high', 'medium', 'low'
 * @param {string} schoolUuid - Optional school UUID filter
 * @returns {Promise<Object>} Quality report with issues
 */
/**
 * Get academic N/A audit — competitive summaries where academic_ratings is N/A
 * but school_academics data exists for that school's state (indicating a code bug,
 * not a genuine data gap).
 * @returns {Promise<Object>} Audit results with affected summaries grouped by state
 */
export const getAcademicNaAudit = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();

    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/academic-na-audit`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch academic N/A audit');
    }

    return await response.json();
  } catch (error) {
    console.error('Academic N/A Audit Error:', error);
    throw error;
  }
};

export const getQualityReport = async (severity = 'all', schoolUuid = null) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();

    const params = new URLSearchParams({ severity });
    if (schoolUuid) {
      params.append('school_uuid', schoolUuid);
    }

    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/quality-report?${params}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get quality report');
    }

    return await response.json();
  } catch (error) {
    console.error('Quality Report Error:', error);
    throw error;
  }
};

// =============================================================================
// ADMIN-ONLY: Multi-School Rollup Summaries
//
// On-demand "portfolio" summaries synthesized across a set of 2+ schools from
// each school's already-generated AI summary (not raw data). Results are
// persisted server-side keyed by a hash of the selected school set, so
// re-selecting the same schools reuses prior generations.
// =============================================================================

/**
 * Check which of the four stored single-school summary types (geographic,
 * competitive, projections, combined) are available for each of the given
 * schools at a drive time/travel mode, so the UI can warn which schools will
 * be skipped before generating a rollup.
 * @param {Array<string>} schoolUuids - School UUIDs to check
 * @param {number} driveTime - Drive time in minutes (5, 10, or 15)
 * @param {string} travelMode - 'drive' or 'walk'
 * @returns {Promise<Object>} { success, drive_time, travel_mode, coverage: { [uuid]: {...} } }
 */
export const getMultiSchoolCoverage = async (schoolUuids, driveTime = 10, travelMode = 'drive') => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();

    const params = new URLSearchParams({
      school_uuids: schoolUuids.join(','),
      drive_time: driveTime.toString(),
      travel_mode: travelMode
    });

    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/multi-school/coverage?${params}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to check multi-school summary coverage');
    }

    return await response.json();
  } catch (error) {
    console.error('Get Multi-School Coverage Error:', error);
    throw error;
  }
};

/**
 * Get any already-generated multi-school rollup summaries for this exact
 * school set/drive time/travel mode, so revisiting a previously generated
 * set loads instantly instead of regenerating.
 * @param {Array<string>} schoolUuids - School UUIDs in the set
 * @param {number} driveTime - Drive time in minutes (5, 10, or 15)
 * @param {string} travelMode - 'drive' or 'walk'
 * @returns {Promise<Object>} { success, school_set_hash, school_uuids, summaries: { [type]: {...} } }
 */
export const getStoredMultiSchoolSummaries = async (schoolUuids, driveTime = 10, travelMode = 'drive') => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();

    const params = new URLSearchParams({
      school_uuids: schoolUuids.join(','),
      drive_time: driveTime.toString(),
      travel_mode: travelMode
    });

    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/multi-school/stored?${params}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch stored multi-school summaries');
    }

    return await response.json();
  } catch (error) {
    console.error('Get Stored Multi-School Summaries Error:', error);
    throw error;
  }
};

/**
 * Generate (and persist) one rollup summary type across a set of schools.
 * Call once per type, in order geographic -> competitive -> projections ->
 * overview, passing the first three results as `priorSummaries` for the
 * 'overview' call so it doesn't need a DB round-trip.
 * @param {Array<string>} schoolUuids - 2-25 school UUIDs
 * @param {string} summaryType - 'overview', 'geographic', 'competitive', or 'projections'
 * @param {number} driveTime - Drive time in minutes (5, 10, or 15)
 * @param {string} travelMode - 'drive' or 'walk'
 * @param {Object} options - Optional: { sourceDetail: 'executive'|'full', verbosity, reasoningEffort, priorSummaries, teamId }
 * @returns {Promise<Object>} Generated summary result, or { error } on failure
 */
export const generateMultiSchoolSummary = async (
  schoolUuids,
  summaryType,
  driveTime = 10,
  travelMode = 'drive',
  options = {}
) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();

    const {
      sourceDetail = 'executive',
      verbosity = 'medium',
      reasoningEffort = 'medium',
      priorSummaries = null,
      teamId = null
    } = options;

    const body = {
      school_uuids: schoolUuids,
      summary_type: summaryType,
      drive_time: driveTime,
      travel_mode: travelMode,
      source_detail: sourceDetail,
      verbosity,
      reasoning_effort: reasoningEffort
    };
    if (priorSummaries) {
      body.prior_summaries = priorSummaries;
    }
    if (teamId) {
      body.team_id = teamId;
    }

    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/multi-school/generate`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      }
    );

    const data = await response.json();

    if (!response.ok) {
      // Surface structured error payloads (e.g. skipped_schools) rather than
      // just a message, so the UI can show which schools were excluded.
      const error = new Error(data.error || `Failed to generate ${summaryType} multi-school summary`);
      error.data = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error(`Generate Multi-School Summary Error (${summaryType}):`, error);
    throw error;
  }
};

/**
 * List recently generated multi-school summary sets, most recent first, so
 * an admin can revisit a rollup without re-selecting the same schools.
 * @param {number} limit - Max number of school sets to return (default 20, max 100)
 * @returns {Promise<Object>} { success, history: [...] }
 */
export const getMultiSchoolHistory = async (limit = 20) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();

    const params = new URLSearchParams({ limit: limit.toString() });

    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/multi-school/history?${params}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch multi-school summary history');
    }

    return await response.json();
  } catch (error) {
    console.error('Get Multi-School History Error:', error);
    throw error;
  }
};
